import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { SERVICE_ICONS, DefaultIcon } from "../lib/icons";
import { ArrowUpRight, MapPin, MessageSquareText } from "lucide-react";
import { waLink } from "../lib/wa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hints, setHints] = useState([]);
  const [sp, setSp] = useSearchParams();
  const activeCat = sp.get("cat") || "all";

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      setServices(r.data.services);
      setCategories(r.data.categories || []);
    });
    fetch(`${API}/config`).then((r) => r.json()).then((c) => setHints(c.non_serviceable_hints || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (activeCat === "all") return services;
    return services.filter((s) => s.category === activeCat);
  }, [services, activeCat]);

  const chips = [{ slug: "all", name: "All", icon: "sparkles" }, ...categories];
  const counts = useMemo(() => {
    const m = { all: services.length };
    for (const s of services) m[s.category] = (m[s.category] || 0) + 1;
    return m;
  }, [services]);

  const setCat = (slug) => {
    if (slug === "all") sp.delete("cat"); else sp.set("cat", slug);
    setSp(sp, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="label-eyebrow">book a real Mate</span>
          <h1 className="font-display text-4xl md:text-6xl font-bold mt-3 leading-tight">All Assists</h1>
        </div>
        <p className="max-w-sm" style={{ color: "var(--text-muted)" }}>
          Choose a category. Set a time. A verified Mate will be on the way — usually within an hour.
        </p>
      </div>

      {/* Category chips — horizontal scroll on mobile, no wrapping */}
      <div className="mt-8 -mx-4 md:-mx-2 px-4 md:px-2 hm-hscroll">
        <div className="flex gap-2 min-w-max" data-testid="service-category-chips">
          {chips.map((c) => {
            const Icon = SERVICE_ICONS[c.icon] || DefaultIcon;
            const isActive = activeCat === c.slug;
            return (
              <button
                key={c.slug}
                data-testid={`cat-chip-${c.slug}`}
                onClick={() => setCat(c.slug)}
                className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-bold transition-all border ${
                  isActive
                    ? "text-white shadow-md"
                    : "bg-white hover:bg-emerald-50"
                }`}
                style={isActive
                  ? { background: "#00604A", borderColor: "#00604A" }
                  : { color: "#00604A", borderColor: "#E4E7E5" }}
              >
                <Icon className="w-3.5 h-3.5" />
                {c.name}
                <span className={`text-[10px] font-normal rounded-full px-1.5 py-0.5 ${isActive ? "bg-white/20" : "bg-emerald-50 text-emerald-700"}`}>
                  {counts[c.slug] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {hints.length > 0 && (
        <div
          data-testid="non-serviceable-banner"
          className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <div className="flex gap-3 items-start">
            <MapPin className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-emerald-900">Not in our list? We still try.</div>
              <div className="text-sm text-emerald-800 mt-0.5">
                Remote or off-network areas like <b>{hints.slice(0, 5).join(", ")}</b> — just WhatsApp us and we'll arrange a Mate.
              </div>
            </div>
          </div>
          <a
            href={waLink("Hi HeylpMate! I need help in a non-serviceable area. Please help.")}
            target="_blank" rel="noreferrer"
            data-testid="non-serviceable-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 transition-colors"
          >
            <MessageSquareText className="w-4 h-4" /> WhatsApp us
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8 stagger" data-testid="services-grid">
        {filtered.map((s) => {
          const Icon = SERVICE_ICONS[s.icon] || DefaultIcon;
          return (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`services-page-card-${s.slug}`}
              className="card-lift group rounded-3xl border border-black/10 bg-white overflow-hidden flex flex-col"
            >
              <div className="relative h-52 md:h-56 overflow-hidden">
                {s.photo && (
                  <img src={s.photo} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />
                <div
                  className="absolute top-3 left-3 w-10 h-10 rounded-full grid place-items-center text-white shadow-lg"
                  style={{ background: "#00604A" }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="absolute top-3 right-3 bg-white rounded-full px-3 py-1 text-xs font-bold shadow-lg" style={{ color: "#00604A" }}>
                  {s.quote_only ? (s.price_inr ? `from ₹${s.price_inr}` : "quote") : `Starting ₹${s.price_inr}`}
                  <span className="text-neutral-400 ml-1 font-normal">/ {s.unit.replace("per ", "").replace("60 min", "hr")}</span>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-display text-2xl" style={{ color: "#00604A" }}>{s.name}</h3>
                <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{s.tagline}</p>
                <p className="text-[11px] text-neutral-500 mt-2">Final price depends on the exact requirement.</p>
                <span className="mt-auto pt-4 inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-xs font-bold text-white shadow-md group-hover:shadow-lg transition"
                      style={{ background: "#F45118" }}>
                  {s.quote_only ? "Get WhatsApp quote" : "Book & Pay"} <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-neutral-500 text-sm">
            No services in this category yet.
          </div>
        )}
      </div>
    </div>
  );
}
